/**
 * ImpersonateModal.jsx · v101.27.0 · Confirmar «Entrar como» un usuario (solo super_admin).
 */
import { useState } from "react";
import { LogIn, Loader2, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { startImpersonation } from "../lib/impersonation";

export default function ImpersonateModal({ user, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user) return null;

  const go = async () => {
    if (reason.trim().length < 5) { toast.error("Escriba el motivo (mínimo 5 caracteres)."); return; }
    setBusy(true);
    try {
      await startImpersonation(user, reason.trim());
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : e?.message || "No se pudo entrar como este usuario.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" data-testid="impersonate-modal">
      <div className="bg-white text-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-[#0A3747] text-white">
          <LogIn size={18} className="text-[#E0E621]" />
          <div className="font-semibold flex-1">Entrar como este usuario</div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="rounded-xl bg-gray-50 border p-3">
            <div className="font-semibold">{user.name || user.email}</div>
            <div className="text-xs text-gray-500">{user.email} · {user.role_display || user.role}</div>
          </div>
          <div className="flex gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            <ShieldAlert size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              Verás y harás todo <b>como este usuario</b> durante máximo 60 minutos. Todo queda registrado a tu nombre
              (inicio, motivo y cada acción). No se puede cambiar su contraseña ni su MFA. Para volver, usa
              «Volver a mi cuenta» en la barra inferior.
            </div>
          </div>
          <label className="block">
            <span className="text-xs font-semibold text-gray-600">Motivo *</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="Ej.: revisar por qué no ve sus OTs asignadas"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white text-gray-800"
              data-testid="impersonate-reason"
            />
          </label>
        </div>
        <div className="px-4 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-gray-300">Cancelar</button>
          <button onClick={go} disabled={busy} className="px-3 py-2 text-sm rounded-lg bg-[#0A3747] text-[#E0E621] font-semibold flex items-center gap-1.5 disabled:opacity-50" data-testid="impersonate-confirm">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />} Entrar como {String(user.name || "").split(" ")[0] || "usuario"}
          </button>
        </div>
      </div>
    </div>
  );
}
